import { useState, useCallback, useEffect } from 'react';
import { Star } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useFavorites } from '../context/FavoritesContext';
import { getFavoritesCache, setFavoritesCache } from '../lib/cache';
import { Mutex } from 'async-mutex';
import clsx from 'clsx';

interface FeaturePageFavoriteButtonProps {
  featureId: string;
  className?: string;
}

// Create a mutex for favorite operations
const favoriteMutex = new Mutex();

// Rate limiting
const RATE_LIMIT_WINDOW = 1000; // 1 second
const MAX_ACTIONS = 1;
const rateLimitMap = new Map<string, { count: number; timestamp: number }>();

function checkRateLimit(userId: string): boolean {
  const now = Date.now();
  const userLimit = rateLimitMap.get(userId);

  if (!userLimit) {
    rateLimitMap.set(userId, { count: 1, timestamp: now });
    return true;
  }

  if (now - userLimit.timestamp > RATE_LIMIT_WINDOW) {
    rateLimitMap.set(userId, { count: 1, timestamp: now });
    return true;
  }

  if (userLimit.count >= MAX_ACTIONS) {
    return false;
  }

  userLimit.count++;
  return true;
}

export function FeaturePageFavoriteButton({ featureId, className = '' }: FeaturePageFavoriteButtonProps) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { triggerRefresh } = useFavorites();
  const [isFavorited, setIsFavorited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!user || !featureId) return;

    // Check cache first
    const cachedData = getFavoritesCache(user.id);
    if (cachedData) {
      const isFavorited = cachedData.favorites.some(f => f.id === featureId);
      setIsFavorited(isFavorited);
      return; // Return early if we have cache data
    }

    // Only fetch from database if cache miss
    const fetchFavoriteStatus = async () => {
      try {
        const { data: favoriteData, error: favoriteError } = await supabase
          .from('user_favorites')
          .select('feature_id')
          .eq('user_id', user.id)
          .eq('feature_id', featureId)
          .maybeSingle();

        if (favoriteError) throw favoriteError;
        
        // Update component state
        setIsFavorited(!!favoriteData);

        // Fetch full favorites data to update cache
        const { data: favorites, error: favoritesError } = await supabase
          .from('user_favorites')
          .select(`
            feature:features (
              id,
              name,
              slug,
              group
            )
          `)
          .eq('user_id', user.id);

        if (favoritesError) throw favoritesError;

        const favoritesData = favorites
          .map(item => item.feature)
          .filter((f): f is NonNullable<typeof f> => f !== null);

        // Update cache
        setFavoritesCache(user.id, {
          favorites: favoritesData,
          timestamp: Date.now()
        });
      } catch (error) {
        console.error('Error checking favorite status:', error);
      }
    };

    fetchFavoriteStatus();
  }, [user, featureId]);

  const toggleFavorite = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      showToast('Please log in to favorite features', 'error');
      return;
    }

    if (isLoading) return;

    // Check rate limit
    if (!checkRateLimit(user.id)) {
      showToast('Please wait before performing another action', 'warning');
      return;
    }

    // Acquire mutex lock
    const release = await favoriteMutex.acquire();

    try {
      setIsLoading(true);

      // Validate user status
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('status')
        .eq('id', user.id)
        .single();

      if (userError || !userData || userData.status !== 'active') {
        throw new Error('User account is not active');
      }

      // Server-side validation of featureId
      const { data: featureData, error: featureError } = await supabase
        .from('features')
        .select('id')
        .eq('id', featureId)
        .eq('is_active', true)
        .is('deleted_at', null)
        .single();

      if (featureError || !featureData) {
        throw new Error('Feature not found or inactive');
      }

      if (isFavorited) {
        const { error: deleteError } = await supabase
          .from('user_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('feature_id', featureId);

        if (deleteError) throw deleteError;
        setIsFavorited(false);
        showToast('Removed from favorites', 'success');
      } else {
        // Check current number of favorites
        const { count, error: countError } = await supabase
          .from('user_favorites')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);

        if (countError) throw countError;

        if (count !== null && count >= 10) {
          showToast('You can only have up to 10 favorites. Please remove some to add more.', 'warning');
          return;
        }

        // Insert with conflict handling
        const { error: insertError } = await supabase
          .from('user_favorites')
          .upsert({
            user_id: user.id,
            feature_id: featureId,
            created_at: new Date().toISOString()
          }, {
            onConflict: 'user_id,feature_id'
          });

        if (insertError) throw insertError;
        setIsFavorited(true);
        showToast('Added to favorites', 'success');
      }

      triggerRefresh();
    } catch (error) {
      console.error('Error toggling favorite:', error);
      showToast(
        error instanceof Error ? error.message : 'Failed to update favorites',
        'error'
      );
    } finally {
      setIsLoading(false);
      release(); // Release mutex lock
    }
  }, [user, featureId, isFavorited, isLoading, showToast, triggerRefresh]);

  return (
    <button
      onClick={toggleFavorite}
      disabled={isLoading}
      className={clsx(
        "p-2 rounded-full transition-colors",
        isFavorited
          ? "text-secondary-500 hover:text-secondary-600"
          : "text-[--text-disabled] hover:text-[--text-secondary]",
        isLoading && "opacity-75",
        className
      )}
      title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
    >
      <Star
        className={clsx(
          "w-6 h-6",
          isFavorited && "fill-current",
          isLoading && "animate-pulse"
        )}
      />
    </button>
  );
}